export type ActivityRowTransition =
  | 'append'
  | 'prepend'
  | 'remove-from-start'
  | 'replace'
  | 'unchanged';

export type ActivityViewportRequest =
  | { index: number | null; key: string; type: 'item' }
  /**
   * `opening` marks the placement that decides where a feed opens, as opposed to a move the reader
   * asked for. Establishing a starting position is never animated.
   */
  | { key: string; opening: boolean; type: 'latest' }
  /** No destination: the feed stays where the reader left it, e.g. below a task's description. */
  | { key: string; type: 'rest' };

export type ActivityViewportCommand =
  | { type: 'none' }
  | { index: number; key: string; smooth: boolean; type: 'center' }
  | { key: string; smooth: boolean; type: 'latest' };

export interface ActivityViewportPlan {
  command: ActivityViewportCommand;
  shift: boolean;
}

interface ActivityViewportPlanInput {
  atEnd: boolean;
  executedCommandKey: string | null;
  fulfilledRequestKey: string | null;
  /**
   * Only a new comment pulls a reader at the end down to it. Status rows arrive while the reader
   * works the panel above (assigning, starting), and following them would yank the scroller.
   */
  isLastRowComment: boolean;
  lastRowId: string | null;
  request: ActivityViewportRequest;
  rowCount: number;
  transition: ActivityRowTransition;
}

interface ViewportRequestInput {
  latestRequestId: number | null;
  navigationHash: string | null;
  navigationScopeId: string;
  navigationVisit: number | null;
  opensAtNewest: boolean;
  targetIndex: number | null;
}

interface ViewportRule {
  match: (input: ActivityViewportPlanInput) => boolean;
  plan: (input: ActivityViewportPlanInput) => ActivityViewportPlan;
}

/** Classifies a row-array update by identity, independent of fresh GraphQL object instances. */
export function classifyActivityRowTransition(
  previousIds: string[],
  currentIds: string[]
): ActivityRowTransition {
  if (getIdsEqual(previousIds, currentIds)) return 'unchanged';
  if (!previousIds.length || !currentIds.length) return 'replace';

  if (currentIds.length > previousIds.length && getRangeEqual(previousIds, currentIds, 0))
    return 'append';

  if (
    currentIds.length > previousIds.length &&
    getRangeEqual(previousIds, currentIds, currentIds.length - previousIds.length)
  )
    return 'prepend';

  if (
    currentIds.length < previousIds.length &&
    getRangeEqual(currentIds, previousIds, previousIds.length - currentIds.length)
  )
    return 'remove-from-start';

  return 'replace';
}

/** Builds the single viewport request for a render from navigation + opening policy. */
export function getViewportRequest({
  latestRequestId,
  navigationHash,
  navigationScopeId,
  navigationVisit,
  opensAtNewest,
  targetIndex,
}: ViewportRequestInput): ActivityViewportRequest {
  if (typeof latestRequestId === 'number')
    return { key: `latest:${latestRequestId}`, opening: false, type: 'latest' };
  if (typeof navigationHash === 'string' && typeof navigationVisit === 'number')
    return {
      index: targetIndex,
      key: `navigation:${navigationScopeId}:${navigationVisit}:${navigationHash}`,
      type: 'item',
    };
  if (opensAtNewest)
    return { key: `initial-latest:${navigationScopeId}`, opening: true, type: 'latest' };
  return { key: `rest:${navigationScopeId}`, type: 'rest' };
}

const VIEWPORT_RULES: ViewportRule[] = [
  {
    match: input =>
      input.request.type === 'item' && input.request.key !== input.fulfilledRequestKey,
    plan: input => {
      if (input.request.type !== 'item') return { command: { type: 'none' }, shift: false };
      return {
        command:
          input.request.index === null
            ? { type: 'none' }
            : {
                index: input.request.index,
                key: input.request.key,
                smooth: getIsArrival(input) ? false : getHasMeasuredContent(input),
                type: 'center',
              },
        shift: false,
      };
    },
  },
  {
    match: input =>
      input.request.type === 'latest' && input.request.key !== input.fulfilledRequestKey,
    plan: input => {
      if (input.request.type !== 'latest') return { command: { type: 'none' }, shift: false };
      /**
       * A reader who asks for the newest row is already looking at measured rows, so this animates
       * even as the feed's first move — a resting feed places nothing when it opens, and its very
       * first move is usually the reader posting.
       */
      const smooth = input.request.opening ? false : getHasMeasuredContent(input);
      return {
        command: input.rowCount
          ? { key: input.request.key, smooth, type: 'latest' }
          : { type: 'none' },
        shift: false,
      };
    },
  },
  {
    match: input => {
      const appendCommandKey = input.lastRowId ? `append:${input.lastRowId}` : null;
      return (
        input.transition === 'append' &&
        input.atEnd &&
        input.isLastRowComment &&
        Boolean(appendCommandKey) &&
        appendCommandKey !== input.executedCommandKey
      );
    },
    plan: input => ({
      command: {
        key: `append:${input.lastRowId}`,
        smooth: getIsArrival(input) ? false : getHasMeasuredContent(input),
        type: 'latest',
      },
      shift: false,
    }),
  },
  {
    match: () => true,
    plan: input => ({
      command: { type: 'none' },
      shift: input.transition === 'prepend' || input.transition === 'remove-from-start',
    }),
  },
];

/**
 * Chooses the one viewport action allowed for a render.
 *
 * Explicit navigation always wins. A missing target intentionally produces no command and disables
 * prepend preservation until the anchored window arrives. A resting feed skips destinations, but
 * still follows new comments and preserves older pages.
 */
export function getActivityViewportPlan(input: ActivityViewportPlanInput): ActivityViewportPlan {
  const rule = VIEWPORT_RULES.find(candidate => candidate.match(input));
  return rule ? rule.plan(input) : { command: { type: 'none' }, shift: false };
}

/**
 * Whether rows the reader has already seen exist to travel across. A window built from scratch has
 * none — every row is unmeasured, so an animation would cross estimated whitespace and then be
 * re-aimed under the reader as the real heights arrive.
 */
function getHasMeasuredContent(input: ActivityViewportPlanInput): boolean {
  return input.transition !== 'replace';
}

/**
 * Whether the viewport is still arriving rather than moving. A session that has yet to place the
 * viewport is showing the reader their first frame of this feed — a deep link landing on a cold load,
 * say — and an arrival should already be at its destination rather than animate toward it.
 */
function getIsArrival(input: ActivityViewportPlanInput): boolean {
  return input.executedCommandKey === null;
}

function getIdsEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && getRangeEqual(left, right, 0);
}

function getRangeEqual(needle: string[], haystack: string[], start: number): boolean {
  return needle.every((id, index) => haystack[start + index] === id);
}
